import { gql } from '@apollo/client';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import Select from 'react-select';
import { z } from 'zod';
import BackToAllActionsButton from '~components/displays/BulkAction/BackToAllActionsButton';
import { InfoAlert, WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { PlaylistType, TimeBlockType } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { useBulkManageUpdateScheduleMutation } from './__generated__/BulkManageSchedule.graphql';
import { BulkActionComponent } from './BulkActionModal';

const scheduleOption = selectOptionBaseSchema;
type ScheduleOption = z.TypeOf<typeof scheduleOption>;

const schema = z.object({
  displayIds: z.array(z.string()),
  schedule: scheduleOption.nullish(),
});
type FormValues = z.TypeOf<typeof schema>;

/**
 * Staff-only counterpart of `BulkManagePowerSchedule`: same bulk action, but backed by
 * `displayBulkUpdateSchedule` and the customer's `schedules`.
 */
export const BulkManageSchedule: BulkActionComponent = ({
  displays,
  customer,
  onCancel,
  onBack,
  onSuccess,
}) => {
  const [bulkUpdateSchedule] = useBulkManageUpdateScheduleMutation();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      schedule: null,
      displayIds: displays.map((d) => d.id),
    },
    resolver: zodResolver(schema),
  });

  const selectedScheduleId = watch('schedule')?.value;
  const selectedSchedule = customer.schedules.find((s) => s.id === selectedScheduleId);

  /**
   * A MIXED playlist only runs on displays that support mixed playlists, so warn as
   * soon as one of the selected displays is image-only.
   */
  const hasUnsupportedPlaylist =
    !!selectedSchedule?.timeBlocks.some(
      (block) =>
        block.type === TimeBlockType.Playlist && block.data.mediaType === PlaylistType.Mixed,
    ) && displays.some((display) => display.playlistType === PlaylistType.Image);

  const overridesBookmark = !!selectedSchedule?.timeBlocks.some(
    (block) => block.type === TimeBlockType.Bookmark,
  );

  const options = useMemo<ScheduleOption[]>(
    () =>
      customer.schedules.map<ScheduleOption>((s) => ({
        label: s.title,
        value: s.id,
      })),
    [customer],
  );
  const { t } = useTranslation();
  const toast = useToast();
  const analytics = useAnalyticsReporter();

  const handleBulkManageSchedule = useCallback(
    async ({ displayIds, schedule }: FormValues) => {
      try {
        await bulkUpdateSchedule({
          variables: {
            input: {
              displayIds,
              scheduleId: schedule?.value,
            },
          },
        });

        if (displayIds.length === 1) {
          analytics.track('scheduleUpdate');
        } else {
          analytics.track('displayBulkActionComplete', {
            action: 'updateSchedule',
            displayCount: displayIds.length,
          });
        }

        await onSuccess();
      } catch (err) {
        toast({
          status: 'error',
          title: t('bulkScheduleUpdateError'),
          description: fromError(err, 'bulkUpdateSchedule'),
        });
      }
    },
    [analytics, bulkUpdateSchedule, onSuccess, toast, t],
  );

  return (
    <ModalContent>
      <ModalHeader>{t('chooseASchedule')}</ModalHeader>
      <ModalCloseButton onClick={onCancel} isDisabled={isSubmitting} />
      <form onSubmit={handleSubmit(handleBulkManageSchedule)}>
        <ModalBody>
          <Stack spacing="4">
            <InfoAlert>{t('schedulePowerOnNotice')}</InfoAlert>
            <InfoAlert>
              <Trans i18nKey="syncScheduleWarn" components={{ strong: <strong /> }} />
            </InfoAlert>
            <FormControl isInvalid={Boolean(errors.schedule)}>
              <FormLabel>{t('chooseSchedule')}</FormLabel>
              <Controller
                name="schedule"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    options={options}
                    components={components}
                    onChange={field.onChange}
                    isLoading={isSubmitting}
                    placeholder={t('clearSchedule')}
                    isMulti={false}
                    escapeClearsValue={false}
                    isClearable={true}
                    isSearchable={true}
                    menuPlacement="auto"
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="schedule"
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FormControl>
            {hasUnsupportedPlaylist && (
              <WarningAlert>{t('schedulePlaylistNotSupportedWarn')}</WarningAlert>
            )}
            {overridesBookmark && <WarningAlert>{t('scheduleBookmarkOverrideWarn')}</WarningAlert>}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <HStack flex="1" alignItems="center">
            <BackToAllActionsButton onBack={onBack} isDisabled={isSubmitting} />
            <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center">
              <Button
                variant="ghost"
                colorScheme="blue"
                onClick={onCancel}
                isDisabled={isSubmitting}
              >
                {t('cancel')}
              </Button>
              <Button
                marginLeft="3"
                type="submit"
                variant="solid"
                colorScheme="blue"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              >
                {t('apply')}
              </Button>
            </Box>
          </HStack>
        </ModalFooter>
      </form>
    </ModalContent>
  );
};

BulkManageSchedule.graphql = {
  fragments: {
    BulkManageSchedule_customer: gql`
      fragment BulkManageSchedule_customer on Customer {
        id
        schedules {
          id
          title
          timeBlocks {
            type
            data {
              mediaType
            }
          }
        }
      }
    `,
  },
  mutations: {
    BulkManageSchedule: gql`
      mutation BulkManageUpdateSchedule($input: DisplayBulkUpdateScheduleInput!) {
        displayBulkUpdateSchedule(input: $input) {
          displays {
            id
            schedule {
              schedule {
                id
                title
              }
            }
          }
        }
      }
    `,
  },
};
